import { Period } from 'screens/Analytics/constants'
import { MetricSnapshot } from 'hooks/queries/externalIntegrations/types'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { last } from 'lodash'

dayjs.extend(isSameOrBefore)

export const getRate = (diff: number) => {
  if (diff === 0) return 'neutral'
  if (diff > 0) return 'positive'
  if (diff < 0) return 'negative'

  return undefined
}

export const EXPANDED_PERIODS: Record<Period, Period> = {
  [Period.LastWeek]: Period.Last2Weeks,
  [Period.LastMonth]: Period.Last2Months,
  [Period.Last3Months]: Period.Last6Months,
  [Period.Last6Months]: Period.LastYear,
  // We don't expand these periods
  [Period.Last2Weeks]: Period.Last2Weeks,
  [Period.Last2Months]: Period.Last2Months,
  [Period.LastYear]: Period.LastYear,
}

export const PERIOD_DURATIONS: Record<Period, number> = {
  [Period.LastWeek]: 7,
  [Period.Last2Weeks]: 14,
  [Period.LastMonth]: 30,
  [Period.Last2Months]: 60,
  [Period.Last3Months]: 90,
  [Period.Last6Months]: 180,
  [Period.LastYear]: 365,
}

export const PERIOD_LABELS: Record<Period, string> = {
  [Period.LastWeek]: '7 days',
  [Period.Last2Weeks]: '14 days',
  [Period.LastMonth]: 'month',
  [Period.Last2Months]: '2 months',
  [Period.Last3Months]: '3 months',
  [Period.Last6Months]: '6 months',
  [Period.LastYear]: 'year',
}

export const lastSnapshotDate = (snapshots: MetricSnapshot[]) => {
  return !!last(snapshots)?.recorded_at ? dayjs(last(snapshots)?.recorded_at).endOf('day') : dayjs().endOf('day')
}

export const sumMetricValues = (snapshots: MetricSnapshot[]) =>
  snapshots.reduce((acc, snapshot) => acc + snapshot.metric_value, 0)

export const calculateRollingPeriodValue = (snapshots: MetricSnapshot[], period: Period) => {
  const totalDays = PERIOD_DURATIONS[period]
  const endDate = lastSnapshotDate(snapshots)
  const startDate = endDate.subtract(totalDays - 1, 'day').startOf('day')

  // Create a map of snapshots by date (YYYY-MM-DD) for fast lookup
  const snapshotMap = new Map<string, MetricSnapshot>()
  snapshots.forEach((snapshot) => {
    const dateKey = dayjs(snapshot.recorded_at).format('YYYY-MM-DD')
    snapshotMap.set(dateKey, snapshot)
  })

  // Generate array with a snapshot for every day in the period
  const completeSnapshots: MetricSnapshot[] = []
  let currentDate = startDate

  while (currentDate.isSameOrBefore(endDate, 'day')) {
    const dateKey = currentDate.format('YYYY-MM-DD')
    const existingSnapshot = snapshotMap.get(dateKey)

    if (existingSnapshot) {
      completeSnapshots.push(existingSnapshot)
    } else {
      // Create an empty snapshot for this date
      const emptySnapshot: MetricSnapshot = {
        id: 0,
        metric_name: snapshots[0]?.metric_name || '',
        metric_value: 0,
        recorded_at: currentDate.toISOString(),
        metric_diffs: [],
      }
      completeSnapshots.push(emptySnapshot)
    }

    currentDate = currentDate.add(1, 'day')
  }

  return completeSnapshots
}

export const getDiffedSnapshots = (snapshots: MetricSnapshot[]) => {
  const diffedSnapshots = snapshots.map((snapshot, index) => {
    if (snapshots.length === 1) return snapshot
    if (index === 0) return { ...snapshot, metric_value: 0 }

    const previousValue = snapshots[index - 1].metric_value
    if (snapshot.metric_value < previousValue) return { ...snapshot, metric_value: 0 }

    return {
      ...snapshot,
      metric_value: snapshot.metric_value - previousValue,
    }
  })

  return diffedSnapshots.slice(1)
}

export const getChartColor = (rate: 'positive' | 'negative' | 'neutral' | undefined) => {
  if (rate === 'positive') {
    return '#20A14F'
  } else if (rate === 'negative') {
    return '#AE2222'
  } else if (rate === 'neutral') {
    return '#B8BFCC'
  } else {
    return undefined
  }
}
